<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Recarga de Gas</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Clientes de Gas
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ clientes.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <div class="hidden lg:flex bg-gray-100 dark:bg-gray-700 p-1 rounded-xl">
          <button @click="viewMode = 'table'" :class="['p-1.5 rounded-lg transition-all', viewMode === 'table' ? 'bg-white dark:bg-gray-600 shadow-sm text-indigo-600' : 'text-gray-500']">
            <Icon icon="mdi:table" class="text-lg" />
          </button>
          <button @click="viewMode = 'cards'" :class="['p-1.5 rounded-lg transition-all', viewMode === 'cards' ? 'bg-white dark:bg-gray-600 shadow-sm text-indigo-600' : 'text-gray-500']">
            <Icon icon="mdi:view-grid" class="text-lg" />
          </button>
        </div>
        <button @click="openCreateModal" class="w-full sm:w-auto px-3 sm:px-5 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-[11px] sm:text-xs tracking-widest transition-all shadow-md flex items-center justify-center gap-1.5 active:scale-95">
          <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
          Nuevo Cliente
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-6">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Buscar</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Cédula, nombre, teléfono..." class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl transition-all outline-none dark:text-white font-bold text-sm" />
          </div>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select v-model="filterStatus" @change="loadClientes" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm">
            <option value="all">TODOS</option>
            <option value="activos">ACTIVOS</option>
            <option value="pendientes">PENDIENTES</option>
          </select>
        </div>
        <div class="sm:col-span-3">
          <button @click="loadClientes" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-200 transition-all flex items-center justify-center gap-1">
            <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" />
            Actualizar
          </button>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-16">
      <Icon icon="mdi:loading" class="animate-spin text-indigo-600" width="40" />
    </div>

    <!-- Empty -->
    <div v-else-if="filteredClientes.length === 0" class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-16 text-center">
      <Icon icon="mdi:account-off-outline" class="text-7xl mb-3 mx-auto opacity-30" />
      <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay clientes de gas</h4>
    </div>

    <!-- Contenido -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">

      <!-- ═══════════════════════════════════════════════════ -->
      <!-- 📱 VISTA MÓVIL: TARJETAS -->
      <!-- ═══════════════════════════════════════════════════ -->
      <div class="lg:hidden p-3 space-y-3">
        <div v-for="c in paginatedClientes" :key="c.idClienteGas"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">

          <!-- Header con estado -->
          <div :class="[
            'px-4 py-2.5 flex items-center justify-between',
            c.estado === 'activo'
              ? 'bg-gradient-to-r from-emerald-500 to-green-500'
              : 'bg-gradient-to-r from-rose-500 to-red-500'
          ]">
            <div class="flex items-center gap-2 min-w-0">
              <Icon icon="mdi:account-circle" class="text-white text-lg flex-shrink-0" />
              <span class="font-black text-white text-sm truncate">
                {{ c.nombre }} {{ c.apellido }}
              </span>
            </div>
            <span class="px-2 py-0.5 bg-white/20 text-white text-[9px] font-black uppercase rounded whitespace-nowrap">
              {{ c.estado }}
            </span>
          </div>

          <!-- Cédula + verificacion -->
          <div class="px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <Icon icon="mdi:card-account-details" class="text-gray-400 text-sm" />
              <span class="font-mono font-black text-sm text-gray-800 dark:text-white">
                {{ c.cedula }}
              </span>
            </div>
            <div>
              <span v-if="c.verificado" class="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 text-[10px] font-black rounded-lg">
                <Icon icon="mdi:check-decagram" class="text-sm" />
                VERIFICADO
              </span>
              <span v-else class="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] font-black rounded-lg">
                <Icon icon="mdi:clock-alert" class="text-sm" />
                PENDIENTE
              </span>
            </div>
          </div>

          <!-- Info de contacto -->
          <div class="px-4 py-3 space-y-2 border-b border-gray-100 dark:border-gray-700">
            <a v-if="c.telefono" :href="`tel:${c.telefono}`" class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 active:opacity-70">
              <div class="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:phone" class="text-blue-600 text-sm" />
              </div>
              <span class="font-bold">{{ c.telefono }}</span>
            </a>
            <a v-if="c.email" :href="`mailto:${c.email}`" class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 active:opacity-70">
              <div class="w-7 h-7 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:email" class="text-purple-600 text-sm" />
              </div>
              <span class="font-bold truncate">{{ c.email }}</span>
            </a>
            <div class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <div class="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:map-marker" class="text-amber-600 text-sm" />
              </div>
              <span class="font-bold">{{ c.nombreZona || 'Sin zona' }}</span>
            </div>
          </div>

          <!-- Botones de acción -->
          <div class="px-3 py-3 bg-gray-50 dark:bg-gray-900/30 grid grid-cols-5 gap-1.5">
            <button v-if="c.telefono" @click="abrirWhatsapp(c)"
              class="py-2.5 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-lg flex flex-col items-center justify-center active:scale-95 transition shadow-sm">
              <Icon icon="mdi:whatsapp" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">Wa</span>
            </button>
            <button @click="verMapa(c)"
              class="py-2.5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg flex flex-col items-center justify-center active:scale-95 transition shadow-sm">
              <Icon icon="mdi:map-marker" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">Mapa</span>
            </button>
            <button @click="openEditModal(c)"
              class="py-2.5 bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-lg flex flex-col items-center justify-center active:scale-95 transition shadow-sm">
              <Icon icon="mdi:pencil" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">Editar</span>
            </button>
            <button v-if="!c.verificado" @click="abrirModalVerificar(c)"
              class="py-2.5 bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-lg flex flex-col items-center justify-center active:scale-95 transition shadow-sm">
              <Icon icon="mdi:check-decagram" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">Verif.</span>
            </button>
            <button v-else disabled
              class="py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-300 dark:text-gray-500 rounded-lg flex flex-col items-center justify-center cursor-not-allowed">
              <Icon icon="mdi:check-circle" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">OK</span>
            </button>
            <button @click="abrirModalEliminar(c)"
              class="py-2.5 bg-gradient-to-br from-rose-500 to-red-600 text-white rounded-lg flex flex-col items-center justify-center active:scale-95 transition shadow-sm">
              <Icon icon="mdi:delete" class="text-base" />
              <span class="text-[8px] font-black uppercase mt-0.5">Borrar</span>
            </button>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════ -->
      <!-- 💻 VISTA ESCRITORIO: TABLA -->
      <!-- ═══════════════════════════════════════════════════ -->
      <div v-if="viewMode === 'table'" class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cliente</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Cédula</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Contacto</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Zona</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Verificado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="c in paginatedClientes" :key="c.idClienteGas" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all">
              <td class="px-5 py-4">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div class="h-10 w-10 rounded-xl flex items-center justify-center shadow-sm bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900">
                      <Icon icon="mdi:gas-cylinder" class="text-lg text-indigo-600 dark:text-indigo-400" />
                    </div>
                  </div>
                  <div class="ml-3">
                    <div class="text-sm font-black text-gray-900 dark:text-white">{{ c.nombre }} {{ c.apellido }}</div>
                    <div class="text-[10px] text-gray-500">ID: {{ c.idClienteGas }}</div>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4"><span class="text-xs font-bold text-gray-900 dark:text-white">{{ c.cedula }}</span></td>
              <td class="px-5 py-4">
                <div class="space-y-1">
                  <div v-if="c.telefono" class="flex items-center text-xs text-gray-700 dark:text-gray-300">
                    <Icon icon="mdi:phone" class="mr-1.5 text-gray-400" />{{ c.telefono }}
                  </div>
                  <div v-if="c.email" class="flex items-center text-xs text-gray-700 dark:text-gray-300">
                    <Icon icon="mdi:email" class="mr-1.5 text-gray-400" /><span class="truncate max-w-[150px]">{{ c.email }}</span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <span class="text-xs font-bold text-gray-700 dark:text-gray-300">{{ c.nombreZona || 'Sin zona' }}</span>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="['px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border', c.estado === 'activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300']">
                  {{ c.estado }}
                </span>
              </td>
              <td class="px-5 py-4 text-center">
                <span v-if="c.verificado" class="text-emerald-600"><Icon icon="mdi:check-decagram" /></span>
                <span v-else class="text-amber-600"><Icon icon="mdi:clock-alert" /></span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button v-if="c.telefono" @click="abrirWhatsapp(c)" title="WhatsApp" class="p-1.5 bg-green-50 dark:bg-green-900/30 text-green-600 rounded-lg hover:bg-green-600 hover:text-white transition-all">
                    <Icon icon="mdi:whatsapp" class="text-base" />
                  </button>
                  <button @click="verMapa(c)" title="Ver mapa" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all">
                    <Icon icon="mdi:map-marker" class="text-base" />
                  </button>
                  <button @click="openEditModal(c)" title="Editar" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 rounded-lg hover:bg-amber-600 hover:text-white transition-all">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button v-if="!c.verificado" @click="abrirModalVerificar(c)" title="Verificar" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 rounded-lg hover:bg-emerald-600 hover:text-white transition-all">
                    <Icon icon="mdi:check-decagram" class="text-base" />
                  </button>
                  <button @click="abrirModalEliminar(c)" title="Eliminar" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-lg hover:bg-rose-600 hover:text-white transition-all">
                    <Icon icon="mdi:delete" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Vista cards en desktop (si el usuario cambia) -->
      <div v-else class="hidden lg:grid grid-cols-2 xl:grid-cols-3 gap-4 p-4">
        <div v-for="c in paginatedClientes" :key="'dc-'+c.idClienteGas"
          class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition">
          <div class="flex items-center justify-between mb-3 pb-2 border-b dark:border-gray-700">
            <div>
              <p class="font-black text-sm text-gray-900 dark:text-white">{{ c.nombre }} {{ c.apellido }}</p>
              <p class="text-[10px] text-gray-500">Cédula: {{ c.cedula }}</p>
            </div>
            <span :class="['px-2 py-0.5 text-[9px] font-black uppercase rounded-lg border', c.estado === 'activo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200']">
              {{ c.estado }}
            </span>
          </div>
          <div class="text-xs space-y-1 mb-3">
            <div v-if="c.telefono" class="flex items-center"><Icon icon="mdi:phone" class="mr-1.5 text-gray-400" />{{ c.telefono }}</div>
            <div v-if="c.email" class="flex items-center"><Icon icon="mdi:email" class="mr-1.5 text-gray-400" />{{ c.email }}</div>
            <div class="flex items-center"><Icon icon="mdi:map-marker" class="mr-1.5 text-gray-400" />{{ c.nombreZona || 'Sin zona' }}</div>
          </div>
          <div class="flex gap-1.5 pt-2 border-t dark:border-gray-700">
            <button @click="openEditModal(c)" class="flex-1 p-2 bg-amber-50 text-amber-600 rounded-lg font-black text-[9px] flex items-center justify-center gap-1">
              <Icon icon="mdi:pencil" /> Editar
            </button>
            <button @click="verMapa(c)" class="flex-1 p-2 bg-blue-50 text-blue-600 rounded-lg font-black text-[9px] flex items-center justify-center gap-1">
              <Icon icon="mdi:map-marker" /> Mapa
            </button>
          </div>
        </div>
      </div>

      <!-- ═══════════════════════════════════════════════════ -->
      <!-- PAGINACIÓN ESTILO GOOGLE -->
      <!-- ═══════════════════════════════════════════════════ -->
      <div v-if="filteredClientes.length > 0"
        class="bg-gray-50/50 dark:bg-gray-900/30 px-3 sm:px-5 py-4 border-t dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">

        <div class="flex items-center gap-2">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-wider hidden sm:inline">Mostrar:</span>
          <button v-for="n in [10, 20, 50, 100]" :key="n"
            @click="itemsPerPage = n; currentPage = 1"
            :class="[
              'px-2.5 py-1 text-[11px] font-black rounded-lg transition-all',
              itemsPerPage === n
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-white dark:bg-gray-800 text-gray-500 border border-gray-200 dark:border-gray-700 hover:border-indigo-400'
            ]">
            {{ n }}
          </button>
        </div>

        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="currentPage = 1" :disabled="currentPage === 1"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-double-left" class="text-base text-gray-600 dark:text-gray-300" />
          </button>
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-left" class="text-base text-gray-600 dark:text-gray-300" />
          </button>

          <button v-for="(pag, idx) in paginasVisibles" :key="'cp-'+idx"
            @click="typeof pag === 'number' && (currentPage = pag)"
            :disabled="pag === '...'"
            :class="[
              'h-9 min-w-[36px] px-2 rounded-lg font-black text-sm transition-all',
              pag === currentPage
                ? 'bg-indigo-600 text-white shadow-md scale-105'
                : pag === '...'
                  ? 'bg-transparent text-gray-400 cursor-default'
                  : 'border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-indigo-400 active:scale-95'
            ]">
            {{ pag }}
          </button>

          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-right" class="text-base text-gray-600 dark:text-gray-300" />
          </button>
          <button @click="currentPage = totalPages" :disabled="currentPage === totalPages"
            class="h-9 w-9 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center hover:border-indigo-400 active:scale-95 transition">
            <Icon icon="mdi:chevron-double-right" class="text-base text-gray-600 dark:text-gray-300" />
          </button>
        </div>

        <span class="text-[10px] font-black text-gray-400 uppercase tracking-wider text-center sm:text-right">
          {{ startItem }}–{{ endItem }} de {{ filteredClientes.length }}
        </span>
      </div>
    </div>

    <!-- Modal Formulario -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9998]" @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center gap-3 sm:rounded-t-2xl">
            <Icon :icon="editingCliente ? 'mdi:pencil-box' : 'mdi:account-plus'" class="text-white text-xl" />
            <h3 class="text-sm font-black text-white">{{ editingCliente ? 'Editar Cliente' : 'Nuevo Cliente' }}</h3>
            <button @click="closeModal" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-80px)]">
            <form @submit.prevent="saveCliente" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Nombre *</label>
                  <input v-model="form.nombre" type="text" required class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Apellido *</label>
                  <input v-model="form.apellido" type="text" required class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
                </div>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Cédula *</label>
                  <input v-model="form.cedula" type="text" required placeholder="V-12345678" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Teléfono *</label>
                  <input v-model="form.telefono" type="tel" required placeholder="0416-1234567" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
                </div>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Email *</label>
                <input v-model="form.email" type="email" required class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Dirección *</label>
                <textarea v-model="form.direccion" rows="2" required class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none resize-none"></textarea>
              </div>
              <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 border border-blue-200 dark:border-blue-800">
                <label class="block text-[10px] font-black text-blue-600 tracking-widest mb-2">🔗 URL del Mapa *</label>
                <input v-model="form.urlMapa" type="url" required placeholder="https://maps.google.com/?q=..." class="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-2 border-blue-100 dark:border-blue-700 focus:border-blue-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
                <p class="text-[9px] text-blue-600 mt-1">Pega el enlace de Google Maps</p>
              </div>
              <div>
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Punto de Referencia</label>
                <input v-model="form.puntoReferencia" type="text" placeholder="Ej: Frente a la panadería..." class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none" />
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Zona</label>
                  <select v-model="form.idZona" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none">
                    <option :value="null">Sin zona</option>
                    <option v-for="z in zonas" :key="z.idZona" :value="z.idZona">{{ z.nombre }}</option>
                  </select>
                </div>
                <div>
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Tipo</label>
                  <select v-model="form.tipoCliente" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-xs font-medium dark:text-white outline-none">
                    <option value="residencial">Residencial</option>
                    <option value="comercial">Comercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                </div>
              </div>
              <div class="flex flex-col sm:flex-row gap-3 pt-4 border-t dark:border-gray-700">
                <button type="button" @click="closeModal" class="w-full sm:flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest hover:bg-gray-50 transition-all">Cancelar</button>
                <button type="submit" :disabled="saving" class="w-full sm:flex-[2] py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{'animate-spin': saving}" />
                  {{ saving ? 'Guardando...' : (editingCliente ? 'Actualizar' : 'Crear') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal Verificar -->
    <Teleport to="body">
      <div v-if="showVerificarModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[9999]" @click.self="showVerificarModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden">
          <div class="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 sm:rounded-t-2xl">
            <h3 class="text-sm font-black text-white flex items-center gap-2">
              <Icon icon="mdi:check-decagram" /> Verificar Cliente
            </h3>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300 mb-4">
              ¿Verificar al cliente <strong>{{ clienteAVerificar?.nombre }} {{ clienteAVerificar?.apellido }}</strong>?
            </p>
            <div>
              <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-1">Observaciones</label>
              <textarea v-model="verificarObservaciones" rows="3" class="w-full px-4 py-2.5 border-2 border-gray-100 dark:border-gray-700 rounded-xl text-xs dark:bg-gray-900/50 dark:text-white outline-none resize-none" placeholder="Opcional..."></textarea>
            </div>
          </div>
          <div class="px-6 py-4 border-t dark:border-gray-700 flex gap-3">
            <button @click="showVerificarModal = false" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs">Cancelar</button>
            <button @click="ejecutarVerificar" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-black text-xs uppercase disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon :icon="saving ? 'mdi:loading' : 'mdi:check-decagram'" :class="{'animate-spin': saving}" />
              {{ saving ? 'Verificando...' : 'Verificar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal Confirmación -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4 z-[10000]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden">
          <div class="px-6 py-4 sm:rounded-t-2xl" :class="confirmModalColor">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmModalIcon" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-sm font-black text-white">{{ confirmModalTitulo }}</h3>
                <p class="text-[10px] text-white/80">{{ confirmModalSubtitulo }}</p>
              </div>
            </div>
          </div>
          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">{{ confirmModalMensaje }}</p>
            <p v-if="confirmModalDetalle" class="text-xs text-gray-500 dark:text-gray-400 mt-2">{{ confirmModalDetalle }}</p>
          </div>
          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t dark:border-gray-700 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs">Cancelar</button>
            <button @click="ejecutarAccionConfirmada" :disabled="confirmModalCargando" class="flex-[2] py-3 rounded-xl font-black text-xs uppercase transition-all flex items-center justify-center gap-2" :class="confirmModalButtonColor">
              <Icon :icon="confirmModalCargando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': confirmModalCargando}" />
              {{ confirmModalCargando ? 'Procesando...' : confirmModalBotonTexto }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";
import {
  obtenerClientesGas, crearClienteGas, actualizarClienteGas,
  eliminarClienteGas, verificarClienteGas, obtenerZonas
} from '@/api/gas.js';

export default {
  name: 'ClientesGasList',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      clientes: [],
      zonas: [],
      loading: false,
      saving: false,
      showModal: false,
      showVerificarModal: false,
      showConfirmModal: false,
      searchQuery: '',
      filterStatus: 'all',
      viewMode: 'table',
      currentPage: 1,
      itemsPerPage: 10,
      editingCliente: null,
      clienteAVerificar: null,
      verificarObservaciones: '',

      confirmModalTitulo: '',
      confirmModalSubtitulo: '',
      confirmModalMensaje: '',
      confirmModalDetalle: '',
      confirmModalIcon: '',
      confirmModalColor: '',
      confirmModalButtonColor: '',
      confirmModalBotonTexto: '',
      confirmModalCargando: false,
      confirmModalAccion: null,

      form: {
        nombre: '', apellido: '', cedula: '', telefono: '', telefonoAlterno: '',
        email: '', direccion: '', urlMapa: '', latitud: null, longitud: null,
        puntoReferencia: '', idZona: null, tipoCliente: 'residencial',
        estado: 'activo'
      }
    };
  },
  computed: {
    filteredClientes() {
      let filtered = [...this.clientes];
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(c =>
          (c.nombre || '').toLowerCase().includes(q) ||
          (c.apellido || '').toLowerCase().includes(q) ||
          (c.cedula || '').toLowerCase().includes(q) ||
          (c.telefono || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
        );
      }
      return filtered;
    },
    paginatedClientes() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredClientes.slice(start, start + this.itemsPerPage);
    },
    totalPages() { return Math.max(1, Math.ceil(this.filteredClientes.length / this.itemsPerPage)); },
    startItem() { return (this.currentPage - 1) * this.itemsPerPage + 1; },
    endItem() { return Math.min(this.currentPage * this.itemsPerPage, this.filteredClientes.length); },

    paginasVisibles() {
      const total = this.totalPages;
      const actual = this.currentPage;
      const max = 7;

      if (total <= max) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      const paginas = [1];
      let inicio = Math.max(2, actual - 2);
      let fin = Math.min(total - 1, actual + 2);

      if (actual <= 4) {
        inicio = 2;
        fin = Math.min(total - 1, 6);
      }

      if (actual >= total - 3) {
        inicio = Math.max(2, total - 5);
        fin = total - 1;
      }

      if (inicio > 2) paginas.push('...');
      for (let i = inicio; i <= fin; i++) paginas.push(i);
      if (fin < total - 1) paginas.push('...');
      paginas.push(total);

      return paginas;
    }
  },
  watch: {
    searchQuery() { this.currentPage = 1; }
  },
  async mounted() {
    await this.loadZonas();
    await this.loadClientes();
  },
  methods: {
    async loadZonas() {
      try {
        const res = await obtenerZonas('activas');
        if (res && res.success) {
          this.zonas = Array.isArray(res.data) ? res.data : [];
        }
      } catch (error) {
        console.error('❌ Error cargando zonas:', error);
      }
    },

    async loadClientes() {
      this.loading = true;
      this.clientes = [];
      try {
        const res = await obtenerClientesGas(this.filterStatus);
        if (!res || typeof res !== 'object') {
          this.toast.error('No se recibió respuesta válida del servidor');
          return;
        }
        if (res.success === true) {
          this.clientes = Array.isArray(res.data) ? res.data : [];
        } else {
          this.toast.error(res.error || 'Error al cargar clientes');
        }
      } catch (error) {
        console.error('💥 Excepción en loadClientes:', error);
        this.toast.error(error?.message || 'Error de conexión');
      } finally {
        this.loading = false;
      }
    },

    openCreateModal() {
      this.editingCliente = null;
      this.form = {
        nombre: '', apellido: '', cedula: '', telefono: '', telefonoAlterno: '',
        email: '', direccion: '', urlMapa: '', latitud: null, longitud: null,
        puntoReferencia: '', idZona: null, tipoCliente: 'residencial', estado: 'activo'
      };
      this.showModal = true;
    },

    openEditModal(c) {
      this.editingCliente = c;
      this.form = { ...c };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async saveCliente() {
      if (!this.form.nombre || !this.form.nombre.trim()) { this.toast.warning('El nombre es obligatorio'); return; }
      if (!this.form.apellido || !this.form.apellido.trim()) { this.toast.warning('El apellido es obligatorio'); return; }
      if (!this.form.cedula || !this.form.cedula.trim()) { this.toast.warning('La cédula es obligatoria'); return; }
      if (!this.form.telefono || !this.form.telefono.trim()) { this.toast.warning('El teléfono es obligatorio'); return; }
      if (!this.form.email || !this.form.email.trim()) { this.toast.warning('El email es obligatorio'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) { this.toast.warning('El email no tiene un formato válido'); return; }
      if (!this.form.direccion || !this.form.direccion.trim()) { this.toast.warning('La dirección es obligatoria'); return; }
      if (!this.form.urlMapa || !this.form.urlMapa.trim()) { this.toast.warning('La URL del mapa es obligatoria'); return; }

      this.saving = true;
      try {
        let res = null;
        if (this.editingCliente) {
          res = await actualizarClienteGas(this.editingCliente.idClienteGas, this.form);
        } else {
          res = await crearClienteGas(this.form);
        }

        if (!res || typeof res !== 'object') {
          this.toast.error('No se recibió respuesta válida del servidor');
          return;
        }

        if (res.success === true) {
          this.toast.success(this.editingCliente ? 'Cliente actualizado' : 'Cliente creado');
          await this.loadClientes();
          this.closeModal();
        } else {
          this.toast.error(res.error || res.message || 'Error al guardar cliente');
        }
      } catch (error) {
        console.error('💥 Excepción en saveCliente:', error);
        this.toast.error(error?.message || 'Error inesperado al guardar');
      } finally {
        this.saving = false;
      }
    },

    abrirModalEliminar(c) {
      this.confirmModalTitulo = 'Eliminar Cliente';
      this.confirmModalSubtitulo = 'Esta acción no se puede deshacer';
      this.confirmModalMensaje = `¿Eliminar a "${c.nombre} ${c.apellido}"?`;
      this.confirmModalDetalle = 'El cliente será eliminado permanentemente.';
      this.confirmModalIcon = 'mdi:alert-circle';
      this.confirmModalColor = 'bg-gradient-to-r from-red-600 to-pink-600';
      this.confirmModalButtonColor = 'bg-gradient-to-r from-red-600 to-pink-600 text-white';
      this.confirmModalBotonTexto = 'Eliminar';
      this.confirmModalAccion = () => this.deleteCliente(c.idClienteGas);
      this.showConfirmModal = true;
    },

    async deleteCliente(id) {
      try {
        const res = await eliminarClienteGas(id);
        if (!res || typeof res !== 'object') { this.toast.error('No se recibió respuesta válida'); return; }
        if (res.success === true) {
          this.toast.success('Cliente eliminado');
          await this.loadClientes();
        } else {
          this.toast.error(res.error || 'Error al eliminar');
        }
      } catch (error) {
        console.error('💥 Excepción:', error);
        this.toast.error(error?.message || 'Error al eliminar');
      }
    },

    abrirModalVerificar(c) {
      this.clienteAVerificar = c;
      this.verificarObservaciones = '';
      this.showVerificarModal = true;
    },

    async ejecutarVerificar() {
      if (!this.clienteAVerificar) return;
      this.saving = true;
      try {
        const res = await verificarClienteGas(this.clienteAVerificar.idClienteGas, {
          verificado: true,
          observaciones: this.verificarObservaciones
        });

        if (!res || typeof res !== 'object') { this.toast.error('No se recibió respuesta válida'); return; }

        if (res.success === true) {
          this.toast.success('Cliente verificado');
          this.showVerificarModal = false;
          this.clienteAVerificar = null;
          await this.loadClientes();
        } else {
          this.toast.error(res.error || 'Error al verificar');
        }
      } catch (error) {
        console.error('💥 Excepción:', error);
        this.toast.error(error?.message || 'Error al verificar');
      } finally {
        this.saving = false;
      }
    },

    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmModalCargando = false;
      this.confirmModalAccion = null;
    },

    async ejecutarAccionConfirmada() {
      if (!this.confirmModalAccion) return;
      this.confirmModalCargando = true;
      try {
        await this.confirmModalAccion();
      } catch (error) {
        console.error('💥 Error:', error);
        this.toast.error('Error al ejecutar la acción');
      } finally {
        this.confirmModalCargando = false;
        this.cancelarConfirmacion();
      }
    },

    abrirWhatsapp(c) {
      if (!c || !c.telefono) { this.toast.warning('Este cliente no tiene teléfono'); return; }
      let phone = c.telefono.replace(/\D/g, '');
      if (phone.length === 10 && !phone.startsWith('58')) phone = '58' + phone;
      if (phone.length === 11 && phone.startsWith('0')) phone = '58' + phone.substring(1);
      const msj = `Hola ${c.nombre}, le escribimos de SYSTEM JJ.`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msj)}`, '_blank');
    },

    verMapa(c) {
      if (c.urlMapa) {
        window.open(c.urlMapa, '_blank');
      } else {
        this.toast.warning('Este cliente no tiene URL de mapa');
      }
    },

    handleSearch: debounce(function () {
      this.currentPage = 1;
    }, 300)
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
</style>